import { computed, inject, Injectable } from '@angular/core';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { withPlaceOrder } from '@libs/mtjr/shared/generated/entities/order/order-store-features';
import { Dtos, Entity, Enums } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { v4 as uuidv4 } from 'uuid';

const CREATE_ORDER_STORAGE_KEY = 'mtjr_create_order_state';

export type OrderItem = {
  product: Entity.Product;
  variation?: Entity.ProductVariation;
  quantity: number;
  finalPrice: number;
};

export type CreateOrderState = {
  selectedItems: OrderItem[];
  paymentMethod: Enums.PaymentMethod;
  notes: string;
};

export const CreateOrderStore = signalStore(
  { providedIn: 'root' },
  withPlaceOrder(),
  withState<CreateOrderState>({
    selectedItems: [],
    paymentMethod: 'cash',
    notes: '',
  }),
  withMethods((store) => ({
    loadFromLocalStorage() {
      try {
        const saved = localStorage.getItem(CREATE_ORDER_STORAGE_KEY);
        if (saved) {
          const state: CreateOrderState = JSON.parse(saved);
          patchState(store, state);
        }
      } catch (error) {
        console.error('Failed to load order state from localStorage:', error);
      }
    },

    saveToLocalStorage() {
      try {
        const state: CreateOrderState = {
          selectedItems: store.selectedItems(),
          paymentMethod: store.paymentMethod(),
          notes: store.notes(),
        };
        localStorage.setItem(CREATE_ORDER_STORAGE_KEY, JSON.stringify(state));
      } catch (error) {
        console.error('Failed to save order state to localStorage:', error);
      }
    },

    clearLocalStorage() {
      try {
        localStorage.removeItem(CREATE_ORDER_STORAGE_KEY);
      } catch (error) {
        console.error('Failed to clear order state from localStorage:', error);
      }
    },
    addProduct(product: Entity.Product, variation?: Entity.ProductVariation) {
      const matchesItem = (item: OrderItem) => {
        const productMatches = item.product.id === product.id;
        const variationMatches = variation ? item.variation?.id === variation.id : !item.variation;
        return productMatches && variationMatches;
      };

      const existingItem = store.selectedItems().find(matchesItem);

      if (existingItem) {
        patchState(store, {
          selectedItems: store
            .selectedItems()
            .map((item) => (matchesItem(item) ? { ...item, quantity: item.quantity + 1 } : item)),
        });
      } else {
        const price = variation?.price ?? product.price ?? 0;
        const discount = variation?.discount ?? product.discount ?? 0;
        const finalPrice = price - (price * discount) / 100;
        patchState(store, {
          selectedItems: [
            ...store.selectedItems(),
            {
              product,
              variation,
              quantity: 1,
              finalPrice,
            },
          ],
        });
      }
      this.saveToLocalStorage();
    },

    updateQuantity(productId: string, variationId: string | undefined | null, quantity: number) {
      const matchesItem = (item: OrderItem) => {
        const productMatches = item.product.id === productId;
        const variationMatches = variationId ? item.variation?.id === variationId : !item.variation;
        return productMatches && variationMatches;
      };

      if (quantity <= 0) {
        patchState(store, {
          selectedItems: store.selectedItems().filter((item) => !matchesItem(item)),
        });
      } else {
        patchState(store, {
          selectedItems: store.selectedItems().map((item) => (matchesItem(item) ? { ...item, quantity } : item)),
        });
      }
      this.saveToLocalStorage();
    },

    removeItem(productId: string, variationId: string | undefined | null) {
      const matchesItem = (item: OrderItem) => {
        const productMatches = item.product.id === productId;
        const variationMatches = variationId ? item.variation?.id === variationId : !item.variation;
        return productMatches && variationMatches;
      };

      patchState(store, {
        selectedItems: store.selectedItems().filter((item) => !matchesItem(item)),
      });
      this.saveToLocalStorage();
    },

    setPaymentMethod(paymentMethod: Enums.PaymentMethod) {
      patchState(store, { paymentMethod });
      this.saveToLocalStorage();
    },

    setNotes(notes: string) {
      patchState(store, { notes });
      this.saveToLocalStorage();
    },

    async submitOrder(placeOrderFn: (body: Dtos.PlaceOrderBody) => Promise<void>) {
      const orderProducts: Dtos.PlaceOrderProduct[] = store.selectedItems().map((item) => {
        const orderProduct: Dtos.PlaceOrderProduct = {
          productId: item.product.id,
          quantity: item.quantity,
          finalPrice: item.finalPrice,
          idempotencyKey: uuidv4(),
        };
        if (item.variation?.id) {
          orderProduct.variationId = item.variation.id;
        }
        return orderProduct;
      });

      await placeOrderFn({
        orderProducts,
        paymentMethod: store.paymentMethod(),
        idempotencyKey: uuidv4(),
        notes: store.notes(),
      });
    },

    reset() {
      patchState(store, {
        selectedItems: [],
        paymentMethod: 'cash',
        notes: '',
      });
      // this.clearLocalStorage();
    },
  })),
  withComputed((store) => ({
    total: computed(() => {
      return store.selectedItems().reduce((sum, item) => sum + item.finalPrice * item.quantity, 0);
    }),
    itemCount: computed(() => {
      return store.selectedItems().reduce((sum, item) => sum + item.quantity, 0);
    }),
  })),
);

@Injectable({
  providedIn: 'root',
})
export class CreateOrderService {
  eventBus = inject(NgEventBus);
  appAlerts = inject(AppAlerts);
  store = inject(CreateOrderStore);

  constructor() {
    // Load saved state from localStorage on initialization
    this.store.loadFromLocalStorage();

    this.eventBus
      .on<{ product: Entity.Product; variation?: Entity.ProductVariation }>('product:addToOrder')
      .subscribe((event) => {
        if (!event.data) {
          return;
        }
        const data = event.data;
        const product = data.product;
        const variation = data.variation;
        const stock = variation?.stock ?? product.stock ?? 0;
        if (stock <= 0) {
          this.appAlerts.dangerSnackbarTR('PRODUCTS.OUT_OF_STOCK');
          return;
        }
        this.store.addProduct(product, variation);
        this.appAlerts.successSnackbarTR('ORDERS.PRODUCT_ADDED');
      });
  }
}
